import React, { useState } from "react";
import {
  autoSignIn,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  signIn,
  signUp,
} from "aws-amplify/auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../config/AuthProvider.js";
import ConfirmSignUpForm from "./ConfirmSignUpForm.jsx";
import ForgotPasswordForm from "./ForgotPasswordForm.jsx";
import LoginForm from "./LoginForm.jsx";
import MFASelectionForm from "./MFASelectionForm.jsx";
import NewPasswordForm from "./NewPasswordForm.jsx";
import ResetPasswordForm from "./ResetPasswordForm.jsx";
import SignUpForm from "./SignUpForm.jsx";
import SmsForm from "./SmsForm.jsx";
import TotpForm from "./TotpForm.jsx";

const LogIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth, checkAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeResponse, setChallengeResponse] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [showSignUp, setShowSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showConfirmSignUp, setShowConfirmSignUp] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginNotice, setLoginNotice] = useState("");
  const [registrationError, setRegistrationError] = useState("");
  const [confirmationNotice, setConfirmationNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const getAuthErrorMessage = (error) => {
    const messages = {
      UsernameExistsException: t("accountExists"),
      InvalidPasswordException: t("passwordDoesNotMeetPolicy"),
      CodeMismatchException: t("invalidConfirmationCode"),
      ExpiredCodeException: t("expiredConfirmationCode"),
      LimitExceededException: t("attemptLimitExceeded"),
      TooManyRequestsException: t("attemptLimitExceeded"),
      UserNotFoundException: t("userDoesNotExist"),
    };

    return messages[error.name] || error.message || t("unexpectedAuthError");
  };

  const navigateSignedInUser = async () => {
    const session = await fetchAuthSession();
    const groups = session.tokens?.idToken?.payload["cognito:groups"] || [];
    await checkAuth();

    if (groups.includes("ADMINS")) {
      setAuth("ADMINS");
      navigate("/admin");
    } else {
      setAuth("USERS");
      navigate("/user");
    }
  };

  const openConfirmation = (notice = "") => {
    setShowSignUp(false);
    setShowForgotPassword(false);
    setShowConfirmSignUp(true);
    setNextStep("");
    setChallengeResponse("");
    setRegistrationError("");
    setConfirmationNotice(notice);
  };

  const returnToLogin = (notice = "") => {
    setShowSignUp(false);
    setShowForgotPassword(false);
    setShowConfirmSignUp(false);
    setNextStep("");
    setChallengeResponse("");
    setPassword("");
    setRegistrationError("");
    setLoginError("");
    setLoginNotice(notice);
  };

  const handleLogin = async () => {
    setLoginError("");
    setLoginNotice("");

    try {
      const response = await signIn({
        username: email.trim().toLowerCase(),
        password,
      });

      switch (response.nextStep?.signInStep) {
        case "DONE":
          await navigateSignedInUser();
          break;
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
        case "RESET_PASSWORD":
          setNextStep(response.nextStep.signInStep);
          break;
        case "CONFIRM_SIGN_UP":
          openConfirmation(t("confirmAccountFirst"));
          break;
        default:
          setLoginError(t("unexpectedAuthError"));
      }
    } catch (error) {
      if (error.name === "UserNotConfirmedException") {
        openConfirmation(t("confirmAccountFirst"));
      } else {
        setLoginError(getAuthErrorMessage(error));
      }
    }
  };

  const handleSignUp = async ({
    name,
    email: registrationEmail,
    password: registrationPassword,
  }) => {
    setRegistrationError("");
    setLoading(true);

    try {
      const response = await signUp({
        username: registrationEmail,
        password: registrationPassword,
        options: {
          userAttributes: {
            email: registrationEmail,
            name,
            picture:
              "https://api.dicebear.com/9.x/avataaars-neutral/svg?seed=Riley",
          },
          autoSignIn: true,
        },
      });

      setEmail(registrationEmail);
      if (response.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        openConfirmation(t("confirmationCodeSent"));
      } else {
        returnToLogin(t("accountCreatedSignIn"));
      }
    } catch (error) {
      setRegistrationError(getAuthErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async () => {
    setRegistrationError("");
    setLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: challengeResponse,
      });

      try {
        const response = await autoSignIn();
        if (response.isSignedIn) {
          await navigateSignedInUser();
          return;
        }
      } catch (error) {
        console.warn("No se pudo iniciar sesión automáticamente:", error);
      }

      returnToLogin(t("accountConfirmedSignIn"));
    } catch (error) {
      setRegistrationError(getAuthErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResendSignUpCode = async () => {
    setRegistrationError("");
    setResending(true);

    try {
      await resendSignUpCode({ username: email });
      setConfirmationNotice(t("confirmationCodeResent"));
    } catch (error) {
      setRegistrationError(getAuthErrorMessage(error));
    } finally {
      setResending(false);
    }
  };

  const handleChallengeResponse = async (responseValue = challengeResponse) => {
    try {
      const response = await confirmSignIn({
        challengeResponse: responseValue,
      });
      if (response.isSignedIn) {
        await navigateSignedInUser();
      } else {
        setNextStep(response.nextStep.signInStep);
      }
    } catch (error) {
      setLoginError(getAuthErrorMessage(error));
    }
  };

  const handleResetPassword = async () => {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: challengeResponse,
        newPassword: password,
      });
      returnToLogin(t("passwordResetSignIn"));
    } catch (error) {
      setLoginError(getAuthErrorMessage(error));
    }
  };

  const renderConfirmationForm = () => (
    <ConfirmSignUpForm
      email={email}
      code={challengeResponse}
      setCode={setChallengeResponse}
      onConfirm={handleConfirmSignUp}
      onResend={handleResendSignUpCode}
      onBack={() => returnToLogin()}
      error={registrationError}
      notice={confirmationNotice}
      loading={loading}
      resending={resending}
    />
  );

  const renderForm = () => {
    if (showSignUp) {
      return (
        <SignUpForm
          onSubmit={handleSignUp}
          onCancel={() => returnToLogin()}
          error={registrationError}
          loading={loading}
        />
      );
    }
    if (showForgotPassword) {
      return (
        <ForgotPasswordForm setShowForgotPassword={setShowForgotPassword} />
      );
    }
    if (showConfirmSignUp) return renderConfirmationForm();

    switch (nextStep) {
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        return (
          <NewPasswordForm
            password={password}
            setPassword={setPassword}
            handleChallengeResponse={handleChallengeResponse}
          />
        );
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
        return (
          <TotpForm
            challengeResponse={challengeResponse}
            setChallengeResponse={setChallengeResponse}
            handleChallengeResponse={handleChallengeResponse}
          />
        );
      case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        return (
          <SmsForm
            challengeResponse={challengeResponse}
            setChallengeResponse={setChallengeResponse}
            handleChallengeResponse={handleChallengeResponse}
          />
        );
      case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
        return (
          <MFASelectionForm handleSelectMFAType={handleChallengeResponse} />
        );
      case "RESET_PASSWORD":
        return (
          <ResetPasswordForm
            handleResetPassword={handleResetPassword}
            challengeResponse={challengeResponse}
            setChallengeResponse={setChallengeResponse}
            password={password}
            setPassword={setPassword}
            error={loginError}
            onBack={() => returnToLogin()}
          />
        );
      case "CONFIRM_SIGN_UP":
        return renderConfirmationForm();
      default:
        return (
          <LoginForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            handleLogin={handleLogin}
            setShowSignUp={() => {
              setRegistrationError("");
              setLoginError("");
              setLoginNotice("");
              setShowSignUp(true);
            }}
            setShowForgotPassword={setShowForgotPassword}
            loginError={loginError}
            loginNotice={loginNotice}
          />
        );
    }
  };

  return renderForm();
};

export default LogIn;
